import React, { createContext, useCallback, useContext, useReducer } from 'react';
import {
    EnquiryPublicActions,
    EnquiryPublicState,
    PublicAction,
} from '../types/enquiry.types';
import { enquiryService } from '../services/enquiryService';
import { EnquiryFormData } from '../validation/enquirySchemas';

export const EnquiryPublicStateContext = createContext<EnquiryPublicState | null>(null);
export const EnquiryPublicActionsContext = createContext<EnquiryPublicActions | null>(null);

const publicInitial: EnquiryPublicState = {
    loading: false,
    submitted: false,
    error: null,
    referenceId: null,
};

function publicReducer(state: EnquiryPublicState, action: PublicAction): EnquiryPublicState {
    switch (action.type) {
        case 'SUBMIT_START':
            return { ...state, loading: true, error: null };
        case 'SUBMIT_SUCCESS':
            return { ...state, loading: false, submitted: true, referenceId: action.payload };
        case 'SUBMIT_ERROR':
            return { ...state, loading: false, error: action.payload };
        case 'RESET':
            return publicInitial;
        default:
            return state;
    }
}

export const EnquiryPublicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(publicReducer, publicInitial);

    const submitEnquiry = useCallback(async (data: EnquiryFormData) => {
        dispatch({ type: 'SUBMIT_START' });
        try {
            const res = await enquiryService.submit(data);
            dispatch({ type: 'SUBMIT_SUCCESS', payload: res.data.referenceId });
        } catch (err: unknown) {
            const e = err as { response?: { data?: { message?: string } } };
            dispatch({
                type: 'SUBMIT_ERROR',
                payload: e.response?.data?.message ?? 'Failed to send enquiry. Please try again.',
            });
        }
    }, []);

    const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

    return (
        <EnquiryPublicStateContext.Provider value={state}>
            <EnquiryPublicActionsContext.Provider value={{ submitEnquiry, reset }}>
                {children}
            </EnquiryPublicActionsContext.Provider>
        </EnquiryPublicStateContext.Provider>
    );
};

export function useEnquiryPublic() {
    const ctx = useContext(EnquiryPublicStateContext);
    if (!ctx) throw new Error('useEnquiryPublic must be used within EnquiryPublicProvider');
    return ctx;
}

export function useEnquiryPublicActions() {
    const ctx = useContext(EnquiryPublicActionsContext);
    if (!ctx) throw new Error('useEnquiryPublicActions must be used within EnquiryPublicProvider');
    return ctx;
}
